import { BridgeKey } from '../interfaces/bridge-key';
import { BridgesIsland } from '../interfaces/bridges-island';

export type BridgesMap = Record<string, number>;

export interface BridgesPoint {
  x: number;
  y: number;
}

export interface BridgesSegment {
  type: 'h' | 'v';
  x?: number;
  y?: number;
  x1?: number;
  x2?: number;
  y1?: number;
  y2?: number;
}

export function sortKey(k: BridgeKey): BridgeKey {
  return k.a < k.b ? k : { a: k.b, b: k.a };
}

export function serializeKey(k: BridgeKey): string {
  return k.a < k.b ? `${k.a}|${k.b}` : `${k.b}|${k.a}`;
}

interface IslandLookup {
  byId: Record<string, BridgesIsland>;
  byCoord: Map<string, BridgesIsland>;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Per-array memo. Every geometry helper used to rebuild an id->island map on
 * each call, and the board calls several of them per island per change
 * detection pass. `islands` is assigned once and treated as immutable, so
 * caching on array identity is safe — a regenerated board produces a new array
 * and therefore a new entry.
 */
const lookupCache = new WeakMap<BridgesIsland[], IslandLookup>();

function lookup(islands: BridgesIsland[]): IslandLookup {
  const cached = lookupCache.get(islands);
  if (cached) return cached;
  const byId: Record<string, BridgesIsland> = {};
  const byCoord = new Map<string, BridgesIsland>();
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of islands) {
    byId[n.id] = n;
    byCoord.set(`${n.x},${n.y}`, n);
    if (n.x < minX) minX = n.x;
    if (n.y < minY) minY = n.y;
    if (n.x > maxX) maxX = n.x;
    if (n.y > maxY) maxY = n.y;
  }
  const built: IslandLookup = { byId, byCoord, minX, minY, maxX, maxY };
  lookupCache.set(islands, built);
  return built;
}

export function buildIslandIndex(islands: BridgesIsland[]): Record<string, BridgesIsland> {
  return lookup(islands).byId;
}

/**
 * Walks from `from` in the given direction and returns the first island hit.
 *
 * Bounded by the island bounding box: no island can exist outside it, so
 * stepping past it can only waste work. The previous version walked to +/-1000
 * grid units doing a linear scan of every island at each step, which on a
 * 12-column board meant ~985 wasted iterations per call.
 */
export function raycast(
  islands: BridgesIsland[],
  from: BridgesIsland,
  dx: number,
  dy: number
): BridgesIsland | null {
  if (dx === 0 && dy === 0) return null;
  const { byCoord, minX, minY, maxX, maxY } = lookup(islands);
  let x = from.x + dx;
  let y = from.y + dy;
  while (x >= minX && x <= maxX && y >= minY && y <= maxY) {
    const hit = byCoord.get(`${x},${y}`);
    if (hit) return hit;
    x += dx;
    y += dy;
  }
  return null;
}

export function computeNeighborPairs(islands: BridgesIsland[]): BridgeKey[] {
  const seen = new Set<string>();
  const result: BridgeKey[] = [];
  for (const n of islands) {
    const horiz = raycast(islands, n, 1, 0);
    const vert = raycast(islands, n, 0, 1);
    for (const target of [horiz, vert]) {
      if (!target) continue;
      const k = sortKey({ a: n.id, b: target.id });
      const s = serializeKey(k);
      if (!seen.has(s)) {
        seen.add(s);
        result.push(k);
      }
    }
  }
  return result;
}

export function computeNeighborPairsFor(islands: BridgesIsland[], n: BridgesIsland): BridgeKey[] {
  const seen = new Set<string>();
  const out: BridgeKey[] = [];
  for (const [dx, dy] of [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as Array<[number, number]>) {
    const target = raycast(islands, n, dx, dy);
    if (!target) continue;
    const k = sortKey({ a: n.id, b: target.id });
    const s = serializeKey(k);
    if (!seen.has(s)) {
      seen.add(s);
      out.push(k);
    }
  }
  return out;
}

export function isVisiblePair(islands: BridgesIsland[], k: BridgeKey): boolean {
  const idx = buildIslandIndex(islands);
  const a = idx[k.a];
  const b = idx[k.b];
  if (!a || !b) return false;
  if (a.x === b.x) {
    const minY = Math.min(a.y, b.y);
    const maxY = Math.max(a.y, b.y);
    return !islands.some((n) => n.x === a.x && n.y > minY && n.y < maxY);
  }
  if (a.y === b.y) {
    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    return !islands.some((n) => n.y === a.y && n.x > minX && n.x < maxX);
  }
  return false;
}

export function normalizeKey(islands: BridgesIsland[], key: BridgeKey): BridgeKey | null {
  const idx = buildIslandIndex(islands);
  const a = idx[key.a];
  const b = idx[key.b];
  if (!a || !b) return null;
  if (!(a.x === b.x || a.y === b.y)) return null;
  const neighbors = computeNeighborPairsFor(islands, a);
  const found = neighbors.find(
    (p) => (p.a === a.id && p.b === b.id) || (p.a === b.id && p.b === a.id)
  );
  if (!found) return null;
  return sortKey({ a: a.id, b: b.id });
}

function segmentOf(islands: BridgesIsland[], k: BridgeKey): BridgesSegment {
  const idx = buildIslandIndex(islands);
  const a = idx[k.a];
  const b = idx[k.b];
  if (a.x === b.x) {
    return { type: 'v', x: a.x, y1: Math.min(a.y, b.y), y2: Math.max(a.y, b.y) };
  }
  return { type: 'h', y: a.y, x1: Math.min(a.x, b.x), x2: Math.max(a.x, b.x) };
}

function segmentsCross(a: BridgesSegment, b: BridgesSegment): boolean {
  if (a.type === 'h' && b.type === 'v') {
    return b.x! > a.x1! && b.x! < a.x2! && a.y! > b.y1! && a.y! < b.y2!;
  }
  if (a.type === 'v' && b.type === 'h') {
    return a.x! > b.x1! && a.x! < b.x2! && b.y! > a.y1! && b.y! < a.y2!;
  }
  return false;
}

export function createsCrossing(
  islands: BridgesIsland[],
  bridgeKeys: BridgeKey[],
  bridges: BridgesMap,
  key: BridgeKey,
  nextCount: number
): boolean {
  if (nextCount <= 0) return false;
  const seg = segmentOf(islands, key);
  const targetKeyS = serializeKey(key);
  for (const otherKey of bridgeKeys) {
    const sk = serializeKey(otherKey);
    if (sk === targetKeyS) continue;
    const cnt = bridges[sk] ?? 0;
    if (cnt === 0) continue;
    if (segmentsCross(seg, segmentOf(islands, otherKey))) return true;
  }
  return false;
}

export interface LineEndpoints {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * `originX`/`originY` are the grid coordinates that map to `boardPadding`.
 * Pass the board's minimum island coordinates so a grid with an empty leading
 * row or column is not drawn with dead space on one side. They default to 0,
 * which is the historical behaviour.
 */
export function lineEndpointsH(
  k: BridgeKey,
  islands: BridgesIsland[],
  cellSize: number,
  boardPadding: number,
  originX = 0,
  originY = 0
): LineEndpoints {
  const idx = buildIslandIndex(islands);
  const a = idx[k.a];
  const b = idx[k.b];
  const y = boardPadding + (a.y - originY) * cellSize;
  const ax = boardPadding + (a.x - originX) * cellSize;
  const bx = boardPadding + (b.x - originX) * cellSize;
  return { x1: Math.min(ax, bx), y1: y, x2: Math.max(ax, bx), y2: y };
}

export function lineEndpointsV(
  k: BridgeKey,
  islands: BridgesIsland[],
  cellSize: number,
  boardPadding: number,
  originX = 0,
  originY = 0
): LineEndpoints {
  const idx = buildIslandIndex(islands);
  const a = idx[k.a];
  const b = idx[k.b];
  const x = boardPadding + (a.x - originX) * cellSize;
  const ay = boardPadding + (a.y - originY) * cellSize;
  const by = boardPadding + (b.y - originY) * cellSize;
  return { x1: x, y1: Math.min(ay, by), x2: x, y2: Math.max(ay, by) };
}

/** Bounding box of the island grid, in grid coordinates. */
export function islandBounds(islands: BridgesIsland[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  if (islands.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  const { minX, minY, maxX, maxY } = lookup(islands);
  return { minX, minY, maxX, maxY };
}

export function getOrientation(islands: BridgesIsland[], k: BridgeKey): 'h' | 'v' | null {
  const idx = buildIslandIndex(islands);
  const a = idx[k.a];
  const b = idx[k.b];
  if (!a || !b) return null;
  if (a.y === b.y) return 'h';
  if (a.x === b.x) return 'v';
  return null;
}

export function currentDegree(bridges: BridgesMap, islandId: string): number {
  let sum = 0;
  for (const sk of Object.keys(bridges)) {
    const cnt = bridges[sk] ?? 0;
    if (cnt === 0) continue;
    const [a, b] = sk.split('|');
    if (a === islandId || b === islandId) sum += cnt;
  }
  return sum;
}

function buildAdjacency(islands: BridgesIsland[], bridges: BridgesMap): Record<string, string[]> {
  const adj: Record<string, string[]> = {};
  islands.forEach((n) => (adj[n.id] = []));
  for (const sk of Object.keys(bridges)) {
    const cnt = bridges[sk] ?? 0;
    if (cnt <= 0) continue;
    const [a, b] = sk.split('|');
    if (adj[a] && adj[b]) {
      adj[a].push(b);
      adj[b].push(a);
    }
  }
  return adj;
}

/**
 * Groups islands into connected components, largest first.
 *
 * Exposed so the board can tell the player *which* islands are stranded. An
 * empty board yields one component per island.
 */
export function connectedComponents(islands: BridgesIsland[], bridges: BridgesMap): string[][] {
  const adj = buildAdjacency(islands, bridges);
  const seen = new Set<string>();
  const components: string[][] = [];
  for (const island of islands) {
    if (seen.has(island.id)) continue;
    const component: string[] = [];
    const stack = [island.id];
    while (stack.length) {
      const cur = stack.pop()!;
      if (seen.has(cur)) continue;
      seen.add(cur);
      component.push(cur);
      for (const nb of adj[cur]) if (!seen.has(nb)) stack.push(nb);
    }
    components.push(component);
  }
  return components.sort((a, b) => b.length - a.length);
}

export function isConnected(islands: BridgesIsland[], bridges: BridgesMap): boolean {
  if (islands.length === 0) return false;
  return connectedComponents(islands, bridges).length === 1;
}

/**
 * How many more bridges island `id` can still accept.
 *
 * Three things bound each neighbouring slot: the hard cap of two bridges per
 * slot, whether an *empty* slot is already blocked by a perpendicular bridge,
 * and how many bridges the neighbour itself still needs. A slot whose neighbour
 * is already satisfied is worth nothing — filling it could only overload that
 * neighbour, so no solution can use it. A slot that already carries a bridge can
 * never be blocked, because crossings are refused at placement time.
 */
export function remainingCapacity(
  islands: BridgesIsland[],
  bridgeKeys: BridgeKey[],
  bridges: BridgesMap,
  id: string
): number {
  const idx = buildIslandIndex(islands);
  const self = idx[id];
  if (!self) return 0;
  let total = 0;
  for (const key of computeNeighborPairsFor(islands, self)) {
    const sk = serializeKey(key);
    const cur = bridges[sk] ?? 0;
    const slotRoom = 2 - cur;
    if (slotRoom <= 0) continue;
    if (cur === 0 && createsCrossing(islands, bridgeKeys, bridges, key, 1)) continue;
    const otherId = key.a === id ? key.b : key.a;
    const other = idx[otherId];
    if (!other) continue;
    const otherNeed = other.requiredBridges - currentDegree(bridges, otherId);
    total += Math.max(0, Math.min(slotRoom, otherNeed));
  }
  return total;
}

/**
 * Whether this island can no longer reach its number — it is short of bridges
 * and every route that could still serve it is used up, blocked or leads to a
 * neighbour that is already full. Provably dead without consulting the
 * solution, so it is safe to show as a rule violation.
 */
export function isStarved(
  islands: BridgesIsland[],
  bridgeKeys: BridgeKey[],
  bridges: BridgesMap,
  island: BridgesIsland
): boolean {
  const degree = currentDegree(bridges, island.id);
  if (degree >= island.requiredBridges) return false;
  return degree + remainingCapacity(islands, bridgeKeys, bridges, island.id) < island.requiredBridges;
}

/**
 * Islands sitting in a closed group: every island in the group already has its
 * exact number of bridges, yet the group is not the whole board.
 *
 * A finished puzzle is connected, so such a group can never be joined to the
 * rest without taking a bridge back — it is a dead end rather than work in
 * progress. Being merely *disconnected* is the normal state for most of a solve
 * and is deliberately not reported here.
 */
export function findClosedStrandedIslands(islands: BridgesIsland[], bridges: BridgesMap): Set<string> {
  const stranded = new Set<string>();
  const components = connectedComponents(islands, bridges);
  if (components.length <= 1) return stranded;
  const idx = buildIslandIndex(islands);
  for (const component of components) {
    const closed = component.every((id) => {
      const n = idx[id];
      return !!n && currentDegree(bridges, id) === n.requiredBridges;
    });
    if (closed) for (const id of component) stranded.add(id);
  }
  return stranded;
}

/**
 * Whether every bridge touching this island matches the solution.
 *
 * Only meaningful once the island has its exact number: below that, a slot that
 * is short of the solution is simply unfinished, not wrong.
 */
export function matchesSolution(
  islands: BridgesIsland[],
  bridges: BridgesMap,
  solution: BridgesMap,
  island: BridgesIsland
): boolean {
  const self = buildIslandIndex(islands)[island.id];
  if (!self) return false;
  for (const key of computeNeighborPairsFor(islands, self)) {
    const sk = serializeKey(key);
    if ((bridges[sk] ?? 0) !== (solution[sk] ?? 0)) return false;
  }
  return true;
}

export function computeProgress(islands: BridgesIsland[], bridges: BridgesMap): number {
  if (islands.length === 0) return 0;
  let totalReq = 0;
  const degree: Record<string, number> = {};
  const effective: Record<string, number> = {};
  for (const n of islands) {
    totalReq += n.requiredBridges;
    degree[n.id] = 0;
    effective[n.id] = 0;
  }
  for (const sk of Object.keys(bridges)) {
    const cnt = bridges[sk] ?? 0;
    if (cnt <= 0) continue;
    const [a, b] = sk.split('|');
    if (degree[a] !== undefined) degree[a] += cnt;
    if (degree[b] !== undefined) degree[b] += cnt;
  }
  const valid: Record<string, boolean> = {};
  for (const n of islands) valid[n.id] = degree[n.id] <= n.requiredBridges;
  for (const sk of Object.keys(bridges)) {
    const cnt = bridges[sk] ?? 0;
    if (cnt <= 0) continue;
    const [a, b] = sk.split('|');
    // Discount only the over-full endpoint. Previously one overloaded island
    // also voided its neighbour's credit, so progress could fall when the
    // player added a bridge that was correct from the neighbour's side.
    if (valid[a] && effective[a] !== undefined) effective[a] += cnt;
    if (valid[b] && effective[b] !== undefined) effective[b] += cnt;
  }
  let totalHave = 0;
  for (const n of islands) totalHave += Math.min(effective[n.id], n.requiredBridges);
  const raw = totalReq > 0 ? totalHave / totalReq : 0;
  // Every island can have its exact count while the layout is still split into
  // separate islands-groups — that is not a finished puzzle, so never report
  // 100% for it. isSolved() remains the authority on completion.
  if (raw >= 1 && !isConnected(islands, bridges)) return 0.95;
  return raw;
}

/**
 * Islands the "Check" button should highlight.
 *
 * Wrong bridge counts take priority. If every count is already exact, the only
 * remaining way to be unsolved is a split layout, so the stranded islands —
 * everything outside the largest component — are highlighted instead. Returns
 * an empty set only when the board is genuinely solved.
 */
export function findCheckErrors(islands: BridgesIsland[], bridges: BridgesMap): Set<string> {
  const errors = new Set<string>();
  for (const n of islands) {
    if (currentDegree(bridges, n.id) !== n.requiredBridges) errors.add(n.id);
  }
  if (errors.size > 0) return errors;

  const [, ...stranded] = connectedComponents(islands, bridges);
  for (const component of stranded) for (const id of component) errors.add(id);
  return errors;
}

export function isSolved(islands: BridgesIsland[], bridges: BridgesMap): boolean {
  if (islands.length === 0) return false;
  for (const n of islands) {
    if (currentDegree(bridges, n.id) !== n.requiredBridges) return false;
  }
  if (!isConnected(islands, bridges)) return false;
  const bridgeKeys = computeNeighborPairs(islands);
  for (const key of bridgeKeys) {
    const cnt = bridges[serializeKey(key)] ?? 0;
    if (cnt === 0) continue;
    if (createsCrossing(islands, bridgeKeys, bridges, key, cnt)) return false;
  }
  return true;
}
